import {
  Box,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppRoutes } from "src/common/constants/constantEnums";
import { UseAppToast } from "src/common/useAppToast";
import type { ConnectionRequestStatus, ConnectionRequestDto } from "src/rest/connectionsService";
import { ConnectionsService } from "src/rest/connectionsService";
import { ProfileService } from "src/rest/profileService";
import { GlobalUtils } from "src/utils/globalUtils";
import type { RootState } from "src/store/store";
import { MKS_DAILY_INTEREST_LIMIT } from "src/utils/env";
import type { MatrimonyConnection } from "src/types/matrimonyConnections.types";
import { ConnectionsInfoSidebar, ConnectionsList } from "src/pages/matrimony/components/connections";
import ReceivedContractRequest from "./ReceivedContractRequest";
import SentContactRequest from "./sentContactRequest";
import { countRecentlyActive, mapConnectionDtoToUi } from "./matrimonyContactRequest.utils";

const MatrimonyContractRequest: React.FC = () => {
  const cardWidth = useBreakpointValue({ base: "100%", md: "700px", xl: "820px" });

  const navigate = useNavigate();
  const { showErrorToast } = UseAppToast();
  const showErrorToastRef = React.useRef(showErrorToast);
  React.useEffect(() => {
    showErrorToastRef.current = showErrorToast;
  }, [showErrorToast]);

  const activeMarriageProfileId = useSelector((state: RootState) => state.globalInfo.activeMarriageProfileId);
  const token = GlobalUtils.getToken();
  const isConsultant = token ? GlobalUtils.getAppRoleFromToken(token) === "CONSULTANT" : false;
  // For consultants use the limit stored at login; fall back to env value for regular users.
  const DAILY_INTEREST_LIMIT = isConsultant
    ? (GlobalUtils.getConsultantInfo()?.dailyRequestCountLimit ?? MKS_DAILY_INTEREST_LIMIT)
    : MKS_DAILY_INTEREST_LIMIT;

  // Default is already 0 (Received) but keep it explicit.
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const activeTab = React.useMemo<"RECEIVED" | "SENT" | "CONNECTIONS">(() => {
    if (activeTabIndex === 0) return "RECEIVED";
    if (activeTabIndex === 1) return "SENT";
    return "CONNECTIONS";
  }, [activeTabIndex]);

  // Track last fetched (profileId + tabIndex) to prevent duplicate calls on re-render.
  const lastFetchedRef = React.useRef<string | null>(null);

  const [marriageProfileId, setMarriageProfileId] = React.useState<string | number | null>(null);
  const resolvedMarriageProfileId = marriageProfileId ?? undefined;
  const [counts, setCounts] = React.useState({ received: 0, sent: 0, accepted: 0 });

  // Received/Sent now use the flat DTO from backend
  const [receivedRequests, setReceivedRequests] = React.useState<ConnectionRequestDto[]>([]);
  const [sentRequests, setSentRequests] = React.useState<ConnectionRequestDto[]>([]);

  // Accepted connections still use the legacy MatrimonyConnection shape used by ConnectionsList
  const [connections, setConnections] = React.useState<MatrimonyConnection[]>([]);
  const [isConnectionsLoading, setIsConnectionsLoading] = React.useState(false);
  const [sentTodayCount, setSentTodayCount] = React.useState(0);

  /**
   * Fetch today's sent-request count from the API.
   * For consultants the active profile's owner email is not directly available from the
   * profile-id, so we fall back to the logged-in consultant's own email — the backend
   * daily-count endpoint is keyed by the account that sent the requests.
   */
  const fetchDailyCount = React.useCallback(async () => {
    try {
      const email = GlobalUtils.getUserLoginInfo()?.emailId;
      if (!email) return;
      const count = await ConnectionsService.getDailyCount(email);
      setSentTodayCount(count);
    } catch {
      // Non-blocking — leave previous value unchanged
    }
  }, []);

  const loadMarriageProfileId = React.useCallback(async () => {
    // For consultants, use the actively selected profile from the switcher.
    if (isConsultant) {
      if (!activeMarriageProfileId) throw new Error("No active profile selected.");
      return activeMarriageProfileId as string | number;
    }
    const email = GlobalUtils.getUserLoginInfo()?.emailId;
    if (!email) throw new Error("Missing logged-in user email");
    const profile = await ProfileService.getProfile(email);
    const id = profile?.marriageProfiles?.[0]?.id;
    if (!id) throw new Error("No marriage profile found for this account.");
    return id as string | number;
  }, [isConsultant, activeMarriageProfileId]);

  // NOTE: `refreshCounts` uses 3 list endpoints (RECEIVED/SENT/ACCEPTED).
  // That causes extra API calls on page load and refresh.
  // To ensure ONLY ONE API call per tab change, we no longer call `refreshCounts`.
  // Instead, we update the count for the active tab from the fetched list length.

  const fetchForTabIndex = React.useCallback(async (id: string | number, tabIndex: number, opts?: { force?: boolean }) => {
    const status: ConnectionRequestStatus = tabIndex === 2 ? "ACCEPTED" : "PENDING";

    const key = `${id}:${status}:${tabIndex}`;
    if (!opts?.force && lastFetchedRef.current === key) return;
    lastFetchedRef.current = key;

    try {
      setIsConnectionsLoading(true);

      if (tabIndex === 0) {
        const raw = await ConnectionsService.getRequestsByMarriageProfileId(id, "PENDING");
        setReceivedRequests(Array.isArray(raw) ? raw : []);
        setCounts((prev) => ({ ...prev, received: Array.isArray(raw) ? raw.length : 0 }));
        return;
      }

      if (tabIndex === 1) {
        const raw = await ConnectionsService.getSentRequests(id);
        setSentRequests(Array.isArray(raw) ? raw : []);
        setCounts((prev) => ({ ...prev, sent: Array.isArray(raw) ? raw.length : 0 }));
        return;
      }

      const raw = await ConnectionsService.getConnectionRequestById(id);
      const mapped = Array.isArray(raw) ? raw.map((d) => mapConnectionDtoToUi(d)) : [];
      setConnections(mapped);
      setCounts((prev) => ({ ...prev, accepted: mapped.length }));
    } catch (e: any) {
      showErrorToastRef.current(e?.response?.data?.message || e?.message || "Unable to load requests.");
      if (tabIndex === 0) setReceivedRequests([]);
      if (tabIndex === 1) setSentRequests([]);
      if (tabIndex === 2) setConnections([]);
      setCounts((prev) => {
        if (tabIndex === 0) return { ...prev, received: 0 };
        if (tabIndex === 1) return { ...prev, sent: 0 };
        return { ...prev, accepted: 0 };
      });
    } finally {
      setIsConnectionsLoading(false);
    }
  }, []);

  const refreshCountsOnInitialLoad = React.useCallback(async (id: string | number) => {
    // Populate counts for all tabs immediately.
    try {
      const [receivedRaw, sentRaw, acceptedRaw] = await Promise.all([
        ConnectionsService.getRequestsByMarriageProfileId(id, "PENDING"),
        ConnectionsService.getSentRequests(id),
        ConnectionsService.getConnectionRequestById(id),
      ]);

      setCounts({
        received: Array.isArray(receivedRaw) ? receivedRaw.length : 0,
        sent: Array.isArray(sentRaw) ? sentRaw.length : 0,
        accepted: Array.isArray(acceptedRaw) ? acceptedRaw.length : 0,
      });

      // Also prime lists so UI (including sidebar sent-today) has data without another call.
      setReceivedRequests(Array.isArray(receivedRaw) ? receivedRaw : []);
      setSentRequests(Array.isArray(sentRaw) ? sentRaw : []);
    } catch (e: any) {
      // Counts are non-blocking; ignore failures here.
      setCounts((prev) => prev);
    }

    // Fetch today's daily count from the dedicated API.
    await fetchDailyCount();
  }, [fetchDailyCount]);

  // initial load: resolve logged-in user's marriageProfileId.
  // For consultants, also re-runs whenever the active profile switcher changes.
  React.useEffect(() => {
    let cancelled = false;

    // Reset stale data immediately so the UI doesn't flash the previous profile's requests.
    setMarriageProfileId(null);
    setReceivedRequests([]);
    setSentRequests([]);
    setConnections([]);
    setCounts({ received: 0, sent: 0, accepted: 0 });
    lastFetchedRef.current = null;

    (async () => {
      try {
        const id = await loadMarriageProfileId();
        if (cancelled) return;
        setMarriageProfileId(id);

        // Initial load: fetch all 3 APIs to populate counts.
        await refreshCountsOnInitialLoad(id);

        // Then ensure the active tab list is fresh (force refresh).
        await fetchForTabIndex(id, activeTabIndex, { force: true });
      } catch (e: any) {
        if (!cancelled) showErrorToastRef.current(e?.response?.data?.message || e?.message || "Unable to load contact requests.");
      }
    })();
    return () => {
      cancelled = true;
    };
  // activeMarriageProfileId drives re-fetch for consultants; loadMarriageProfileId already
  // captures it in its own deps so including it here ensures the effect re-runs on switch.
  }, [activeMarriageProfileId, activeTabIndex, fetchForTabIndex, loadMarriageProfileId, refreshCountsOnInitialLoad]);

  // Trigger API ONLY when tab index changes (and id exists).
  // Also re-fetch the daily count when landing on the Sent tab.
  React.useEffect(() => {
    if (!marriageProfileId) return;
    void fetchForTabIndex(marriageProfileId, activeTabIndex, { force: true });
    if (activeTabIndex === 1) void fetchDailyCount();
  }, [activeTabIndex, marriageProfileId, fetchForTabIndex, fetchDailyCount]);

  const connectionsInfo = React.useMemo(
    () => ({
      totalConnections: counts.accepted,
      recentlyActive: countRecentlyActive(connections),
      sentToday: sentTodayCount,
      dailyLimit: DAILY_INTEREST_LIMIT,
    }),
    [connections, counts.accepted, sentTodayCount]
  );

  const getOtherProfileIdFromConnection = React.useCallback(
    (conn: MatrimonyConnection): string | number | null => {
      if (!marriageProfileId) return null;
      const yourId = String(marriageProfileId);

      const senderId = conn.senderProfileId != null ? String(conn.senderProfileId) : null;
      const receiverId = conn.receiverProfileId != null ? String(conn.receiverProfileId) : null;

      if (!senderId || !receiverId) return null;

      // If I'm the sender, other is receiver; otherwise other is sender.
      const otherId = senderId === yourId ? receiverId : senderId;
      if (!otherId || otherId === yourId) return null;

      // Keep original primitive type if possible
      return otherId;
    },
    [marriageProfileId]
  );

  const handleViewProfile = (arg: string | number | MatrimonyConnection) => {
    // Accept both historical (profile.id) and new (full connection) call sites.
    const conn = typeof arg === "object" ? (arg as MatrimonyConnection) : null;
    const legacyId = typeof arg === "object" ? conn?.profile?.id : arg;

    const otherProfileId = conn ? getOtherProfileIdFromConnection(conn) : null;

    // My Connections-only full profile flow (hard gate)
    if (marriageProfileId && otherProfileId != null) {
      navigate(
        `${AppRoutes.MATRIMONY_PROFILE_DETAILS}?fromConnections=1&requestProfileId=${encodeURIComponent(
          String(otherProfileId)
        )}&yourProfileId=${encodeURIComponent(String(marriageProfileId))}`
      );
      return;
    }

    // Existing behavior (fallback)
    navigate(`${AppRoutes.MATRIMONY_PROFILE_DETAILS}?id=${encodeURIComponent(String(legacyId))}`);
  };

  // Underline-only tabs (no boxed bg/pill)
  const tabBaseProps = {
    color: "#4A2C2A",
    fontWeight: { base: 500, md: 400 },
    fontSize: { base: "md", sm: "lg", md: "xl" },
    px: { base: 2, md: 4 },
    py: { base: 3, md: 2 },
    bg: "transparent",
    borderRadius: 0,
    minW: { base: "auto", sm: "120px" },
    whiteSpace: "nowrap" as const,
  };

  const Sidebar = (
    <Box
      minW={{ base: "auto", md: "320px" }}
      maxW={{ base: "100%", md: "350px" }}
      w={{ base: "100%", md: "350px" }}
      position={{ base: "static", md: "sticky" }}
      top={{ md: "84px" }}
      alignSelf={{ md: "flex-start" }}
      pt={0}
    >
      <ConnectionsInfoSidebar activeTab={activeTab} connectionsInfo={connectionsInfo} />
    </Box>
  );

  const ListContent = (
    <Box
      width={cardWidth}
      maxW="100%"
      minW={0}
      borderRadius="12px"
      boxShadow="0 10px 25px rgba(18, 18, 18, 0.06)"
      overflow="hidden"
      p={{ base: 0, md: 0 }}
      bg="white"
    >
      <Tabs index={activeTabIndex} onChange={setActiveTabIndex}>
        <TabList
          justifyContent="start"
          borderBottomWidth="1px"
          borderColor="#E5DDD5"
          px={{ base: 2, md: 3 }}
          pt={4}
          overflowX={{ base: "auto", md: "visible" }}
        >
          <Tab
            {...tabBaseProps}
            border="none"
            borderBottomWidth="3px"
            borderBottomStyle="solid"
            borderBottomColor="transparent"
            _selected={{
              color: "#4A2C2A",
              fontWeight: 600,
              borderBottomColor: "#4A2C2A",
            }}
          >
            <Text as="span">
              Received <Text as="span" fontWeight={500}>
                ({counts.received})
              </Text>
            </Text>
          </Tab>

          <Tab
            {...tabBaseProps}
            border="none"
            borderBottomWidth="3px"
            borderBottomStyle="solid"
            borderBottomColor="transparent"
            _selected={{
              color: "#4A2C2A",
              fontWeight: 600,
              borderBottomColor: "#4A2C2A",
            }}
          >
            <Text as="span">
              Sent <Text as="span" fontWeight={500}>
                ({counts.sent})
              </Text>
            </Text>
          </Tab>

          <Tab
            {...tabBaseProps}
            border="none"
            borderBottomWidth="3px"
            borderBottomStyle="solid"
            borderBottomColor="transparent"
            _selected={{
              color: "#4A2C2A",
              fontWeight: 600,
              borderBottomColor: "#4A2C2A",
            }}
          >
            <Text as="span">
              My Connections <Text as="span" fontWeight={500}>
                ({counts.accepted})
              </Text>
            </Text>
          </Tab>
        </TabList>

        <TabPanels px={{ base: 6, md: 8 }} pb={6}>
          <TabPanel px={0}>
            {resolvedMarriageProfileId ? (
              <ReceivedContractRequest
                marriageProfileId={resolvedMarriageProfileId}
                items={receivedRequests}
                isLoading={isConnectionsLoading}
                onRefresh={() => {
                  void fetchForTabIndex(resolvedMarriageProfileId, 0, { force: true });
                }}
                onAcceptedNavigateToConnections={() => {
                  setActiveTabIndex(2);
                }}
              />
            ) : null}
          </TabPanel>

          <TabPanel px={0}>
            {resolvedMarriageProfileId ? (
              <SentContactRequest
                marriageProfileId={resolvedMarriageProfileId}
                items={sentRequests}
                isLoading={isConnectionsLoading}
                onRefresh={() => {
                  void fetchForTabIndex(resolvedMarriageProfileId, 1, { force: true });
                }}
              />
            ) : null}
          </TabPanel>

          <TabPanel px={0}>
            <ConnectionsList // Pass full connection so we can compute otherProfileId reliably
              items={connections}
              isLoading={isConnectionsLoading}
              onViewProfile={(idOrProfileId) => {
                const found = connections.find((c) => String(c.profile.id) === String(idOrProfileId));
                handleViewProfile(found ?? idOrProfileId);
              }} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );

  return (
    <Stack
      w="100%"
      direction={{ base: "column", md: "row" }}
      minH="calc(100vh - 120px)"
      pt={{ base: 16, md: 16 }}
      pb={8}
      px={{ base: 4, md: 6, xl: 8 }}
      gap={{ base: 4, md: 6, xl: 8 }}
      align="stretch"
    >
      {/* Mobile: sidebar first. md+: list left, sidebar right */}
      <Box display={{ base: "block", md: "none" }}>{Sidebar}</Box>
      <Box flex="1" minW={0}>
        {ListContent}
      </Box>
      <Box display={{ base: "none", md: "block" }}>{Sidebar}</Box>
    </Stack>
  );
};

export default MatrimonyContractRequest;
